/**
 * IPC event payload mappings and type definitions
 * Safe for import in both main and renderer processes
 */

import { z } from 'zod';

import {
  MeetingNavigatePayload,
  MeetingStateChangePayload,
  MeetingWindowFocusChangePayload,
} from '../../src/features/meetings/meetings.types';
import {
  AcknowledgePayload,
  // Inbound payloads
  ApiSenderPayload,
  ExcludeAppPayload,
  ExcludeDomainPayload,
  ExclusionUpdatePayload,
  FrontAppContextPayload,
  IncludeAppPayload,
  IncludeDomainPayload,
  MeetingAudioEnergyLevelsPayload,
  ObserverStatusPayload,
  PauseContextCollectionPayload,
  PermissionsStatusPayload,
  PingPayload,
  SpriteUpdatePayload,
  SystemEventPayload,
  TraversedContentPayload,
  ModifierTapDetectedPayload,
  ModifierHoldEventPayload,
  MentionableWindowsPayload,
  CapturableWindowsPayload,
  AppScreenshotCapturedPayload,
  GetSnapshotDumpAppsPayload,
  GetSnapshotDumpArchivesPayload,
  SnapshotDumpAppsPayload,
  SnapshotDumpArchivesPayload,
} from '../main/ipcBridge/ipcInboundPayloads';
import {
  // Outbound payloads
  ApiConfigPayload,
  AuthTokensExpiredPayload,
  ClearAuthPayload,
  ConfigureModifierTapMonitorPayload,
  ContentFiltersUpdatePayload,
  DeleteContextPayload,
  StopModifierTapMonitorPayload,
  PasteTextPayload,
  RunObserverPayload,
  SubscriptionPlanPayload,
  TraverseAppPayload,
  UpdateStatusPayload,
  UserEmailPayload,
} from '../main/ipcBridge/ipcOutboundPayloads';

import { SetAppVisibilityModePayloadSchema } from './app.types';
import { BackendChoicePayloadSchema } from './backend-choice.types';
import {
  DictationAudioLevelPayloadSchema,
  DictationSessionStatePayloadSchema,
  DictationTranscriptPayloadSchema,
  StartDictationPayloadSchema,
  StopDictationPayloadSchema,
} from './dictation.types';
import { FeatureFlagsPayloadSchema } from './feature-flags.types';
import { HummingbirdOpenThreadPayloadSchema } from './hummingbird.types';
import { IPCEventType } from './ipc.types';
import {
  NativeNotificationSchema,
  LbNotificationSchema,
  NotificationActionEventSchema,
  NotificationIdSchema,
  NotificationInteractionEventSchema,
} from './lb-notification.types';
import { ContextKitMeetingDebugEventSchema } from './meeting-debug.types';
import {
  LocalTranscriptionReadinessSchema,
  MeetingAppsUpdatedPayload,
  MeetingParticipantsUpdatedPayloadSchema,
  MeetingSpeakerNameResolvedPayloadSchema,
  MeetingInterruptPayload,
  MeetingIntentDetectedPayloadSchema,
  MeetingIntentOverlayResolutionSchema,
  MeetingIdPayloadSchema,
  MeetingStoppedPayloadSchema,
  MeetingMenubarCalendarConfigSchema,
  MeetingMenubarCalendarDataSchema,
  MeetingCalendarEventRequestPayloadSchema,
  MeetingRequestStopNotePayloadSchema,
  MeetingStartInputSilentPayloadSchema,
  MeetingTranscriptionInputDevicesPayloadSchema,
  MeetingPostProcessingJobReferencePayloadSchema,
  MeetingPostProcessingStartPayloadSchema,
  MeetingPostProcessingStateChangedPayloadSchema,
  MeetingSegmentEventPayload,
  StartNoteTakingEventPayloadSchema,
  StopNoteTakingRequestPayloadSchema,
  StartMeetingInputSchema,
  TranscriptDeltaPayloadSchema,
} from './meetings.types';
import { PlatformActionInvocationPayloadSchema } from './platform-actions.types';
import { MainWindowNavigateToSchema } from './window.types';

const SyncExclusionsPayloadSchema = z.object({
  excludedAppBundleIds: z.array(z.string()),
  appAccessMode: z.enum(['block', 'allow']),
  allowedAppBundleIds: z.array(z.string()),
  excludedDomains: z.array(z.string()),
  userIncludedDomains: z.array(z.string()),
  enabledCategoryIds: z.array(z.string()),
});

const SetMeetingDetectionDisabledAppsPayloadSchema = z.object({
  disabledBundleKeys: z.array(z.string()),
});

const SetMeetingDetectionDisabledSitesPayloadSchema = z.object({
  disabledHosts: z.array(z.string()),
});

export const MeetingSpeakerSightingsPayloadSchema = z.object({
  names: z.array(z.string()),
  atMs: z.number().int(),
});
export type MeetingSpeakerSightingsPayload = z.infer<
  typeof MeetingSpeakerSightingsPayloadSchema
>;

const ReadabilityIssuePayloadSchema = z.object({
  issue: z.enum([
    'google-screenreader-not-enabled',
    'vscode-family-screenreader-not-enabled',
  ]),
  hasScreenshotFallback: z.boolean(),
  /** Required for `vscode-family-screenreader-not-enabled`. Disambiguates VS Code
   *  from Cursor (and any future VS Code-family editor) so the UI can pick
   *  app-specific copy at toast-render time. */
  bundleIdentifier: z.string().optional(),
});

const DebugSnapshotWindowResultSchema = z.object({
  title: z.string(),
  domain: z.string().nullable(),
  outcome: z.enum(['included', 'no_result', 'skipped', 'failed']),
  durationMs: z.number().nullable(),
  failureReason: z.string().nullable(),
  skipReason: z.string().nullable(),
  parserName: z.string().nullable(),
});

const DebugSnapshotMetricsSchema = z.object({
  totalDurationMs: z.number(),
  parsingDurationMs: z.number().nullable(),
  windowsProcessed: z.number(),
  windowsFailed: z.number(),
  windowsSkipped: z.number(),
  windowsNotAttemptedDueToDeadline: z.number(),
  traversedItemsDeduplicated: z.number(),
  parsedItemsDeduplicated: z.number(),
  screenshotsDeduplicated: z.number(),
});

const DebugSnapshotOutcomeSchema = z.enum([
  'success',
  'empty',
  'failed',
  'cancelled',
  'timeout',
  'partial_timeout',
]);

const DebugSnapshotReportPayloadSchema = z.object({
  jobId: z.string(),
  appName: z.string(),
  bundleId: z.string(),
  pid: z.number(),
  trigger: z.string(),
  outcome: DebugSnapshotOutcomeSchema,
  createdAt: z.number(),
  completedAt: z.number(),
  metrics: DebugSnapshotMetricsSchema,
  windows: z.array(DebugSnapshotWindowResultSchema),
  includedDomains: z.array(z.string()),
  jobFailure: z.string().nullable(),
});

export const ExternalLinkRequestedPayloadSchema = z.object({
  url: z.string(),
});

// When adding new events, follow these steps:
// 1. Add the event type to the IPCEventType enum in shared/ipc.types.ts
// 2. Add the event value to either inboundEventTypes or outboundEventTypes Set below
// 3. If the event requires a payload, add the event/response mapping to ipcEventTypeToPayloadMap
// 4. If requestContextKit should validate the outbound request shape, add a request schema to ipcEventTypeToRequestPayloadMap
// Note: The inboundKeys and outboundKeys arrays are automatically generated from the Sets

export const ipcEventTypeToPayloadMap = {
  // Shared ping - using inbound payload schema as it's the same
  [IPCEventType.ping]: PingPayload,
  [IPCEventType.isObserverRunning]: ObserverStatusPayload,
  [IPCEventType.acknowledge]: AcknowledgePayload,
  // Inbound event payloads
  [IPCEventType.excludeApp]: ExcludeAppPayload,
  [IPCEventType.excludeDomain]: ExcludeDomainPayload,
  [IPCEventType.includeApp]: IncludeAppPayload,
  [IPCEventType.includeDomain]: IncludeDomainPayload,
  [IPCEventType.meetingAppsUpdated]: MeetingAppsUpdatedPayload,
  [IPCEventType.meetingParticipantsUpdated]:
    MeetingParticipantsUpdatedPayloadSchema,
  [IPCEventType.meetingSpeakerNameResolved]:
    MeetingSpeakerNameResolvedPayloadSchema,
  [IPCEventType.meetingSegmentEvent]: MeetingSegmentEventPayload,
  [IPCEventType.contextKitDidCrash]: null,
  [IPCEventType.contextKitDidRecover]: null,
  [IPCEventType.externalLinkRequested]: ExternalLinkRequestedPayloadSchema,
  [IPCEventType.meetingStarted]: MeetingIdPayloadSchema,
  [IPCEventType.meetingStopped]: MeetingStoppedPayloadSchema,
  [IPCEventType.meetingDebugEvent]: ContextKitMeetingDebugEventSchema,
  [IPCEventType.meetingLocalTranscriptionReadinessChanged]:
    LocalTranscriptionReadinessSchema,
  [IPCEventType.meetingTranscriptionInputDeviceChanged]:
    MeetingTranscriptionInputDevicesPayloadSchema,
  [IPCEventType.meetingPostProcessingStateChanged]:
    MeetingPostProcessingStateChangedPayloadSchema,
  [IPCEventType.meetingOpenChat]: MeetingNavigatePayload,
  [IPCEventType.meetingMainWindowFocusChange]: MeetingWindowFocusChangePayload,
  [IPCEventType.meetingStatusWindowMoved]: null,
  [IPCEventType.meetingStatusWindowShouldCollapse]: z
    .boolean()
    .optional()
    .default(false),
  [IPCEventType.meetingRequestQuickNote]: null,
  [IPCEventType.meetingRequestPrepCalendarEvent]:
    MeetingCalendarEventRequestPayloadSchema,
  [IPCEventType.meetingRequestStartCalendarEvent]:
    MeetingCalendarEventRequestPayloadSchema,
  [IPCEventType.meetingRequestStartNote]: StartMeetingInputSchema,
  [IPCEventType.meetingMenubarCalendarConfigUpdated]:
    MeetingMenubarCalendarConfigSchema,
  [IPCEventType.meetingMenubarCalendarDataUpdated]:
    MeetingMenubarCalendarDataSchema,
  [IPCEventType.meetingRequestStopNote]: MeetingRequestStopNotePayloadSchema,
  [IPCEventType.meetingStateDidChange]: MeetingStateChangePayload,
  [IPCEventType.meetingInterrupt]: MeetingInterruptPayload,
  [IPCEventType.meetingStartInputSilent]: MeetingStartInputSilentPayloadSchema,
  [IPCEventType.quitBlockedByMeeting]: null,
  [IPCEventType.systemEventReceived]: SystemEventPayload,
  [IPCEventType.meetingAudioEnergyLevelsReceived]:
    MeetingAudioEnergyLevelsPayload,
  [IPCEventType.spriteUpdated]: SpriteUpdatePayload,
  [IPCEventType.apiSenderEventReceived]: ApiSenderPayload,
  [IPCEventType.exclusionUpdated]: ExclusionUpdatePayload,
  [IPCEventType.requestAuthToken]: null,
  [IPCEventType.pauseContextCollection]: PauseContextCollectionPayload,
  [IPCEventType.disableContextCollection]: null,
  [IPCEventType.enableContextCollection]: null,
  [IPCEventType.traversedAppContent]: TraversedContentPayload,
  [IPCEventType.frontAppContext]: FrontAppContextPayload,
  [IPCEventType.resourcesSnapshotReceived]: null,
  [IPCEventType.readabilityIssue]: ReadabilityIssuePayloadSchema,
  [IPCEventType.debugSnapshotReport]: DebugSnapshotReportPayloadSchema,
  [IPCEventType.lbNotificationsQueueNotification]: LbNotificationSchema,
  [IPCEventType.lbNotificationsSendNativeNotification]:
    NativeNotificationSchema,
  [IPCEventType.lbNotificationsClearAll]: null,
  [IPCEventType.lbNotificationsQueueCount]: null,
  [IPCEventType.lbNotificationsHasNotification]: NotificationIdSchema,
  [IPCEventType.lbNotificationsRemoveQueuedById]: NotificationIdSchema,
  [IPCEventType.lbNotificationsDismissById]: NotificationIdSchema,
  [IPCEventType.lbNotificationsGetVolume]: null,
  [IPCEventType.lbNotificationsSetVolume]: z.number(),
  [IPCEventType.lbNotificationsGetEnabled]: null,
  [IPCEventType.lbNotificationsSetEnabled]: z.boolean(),
  [IPCEventType.lbNotificationsSetSoundChoice]: z.string(),
  [IPCEventType.lbNotificationsPreviewSoundChoice]: z.string(),
  [IPCEventType.lbNotificationsWillNotify]: LbNotificationSchema,
  [IPCEventType.lbNotificationsDidPresent]: LbNotificationSchema,
  [IPCEventType.lbNotificationsDidNotify]: NotificationIdSchema.optional(),
  [IPCEventType.lbNotificationsQueueChanged]: z.number(),
  [IPCEventType.lbNotificationsAction]: NotificationActionEventSchema,
  [IPCEventType.lbNotificationsInteractionChanged]:
    NotificationInteractionEventSchema,
  [IPCEventType.lbNotificationsRequestDismissNotification]: null,
  [IPCEventType.mainWindowNavigateToRoute]: MainWindowNavigateToSchema,
  [IPCEventType.mainWindowEnterFullScreen]: null,
  [IPCEventType.mainWindowLeaveFullScreen]: null,
  [IPCEventType.getAppVisibilityMode]: null,
  [IPCEventType.setAppVisibilityMode]: SetAppVisibilityModePayloadSchema,
  [IPCEventType.hummingbirdBecomeKeyWindow]: null,
  [IPCEventType.hummingbirdOpenThread]: HummingbirdOpenThreadPayloadSchema,
  [IPCEventType.modifierTapDetected]: ModifierTapDetectedPayload,
  [IPCEventType.modifierHoldStarted]: ModifierHoldEventPayload,
  [IPCEventType.modifierHoldReleased]: ModifierHoldEventPayload,
  // Outbound event payloads
  [IPCEventType.configureModifierTapMonitor]:
    ConfigureModifierTapMonitorPayload,
  [IPCEventType.startModifierTapMonitor]: null,
  [IPCEventType.stopModifierTapMonitor]: StopModifierTapMonitorPayload,
  [IPCEventType.updateContentFilters]: ContentFiltersUpdatePayload,
  [IPCEventType.syncExclusions]: SyncExclusionsPayloadSchema,
  [IPCEventType.setMeetingDetectionDisabledApps]:
    SetMeetingDetectionDisabledAppsPayloadSchema,
  [IPCEventType.setMeetingDetectionDisabledSites]:
    SetMeetingDetectionDisabledSitesPayloadSchema,
  [IPCEventType.runObserver]: RunObserverPayload,
  [IPCEventType.stopObserver]: null,
  [IPCEventType.setTokenAndUrl]: ApiConfigPayload,
  [IPCEventType.setUserEmail]: UserEmailPayload,
  [IPCEventType.setFeatureFlags]: FeatureFlagsPayloadSchema,
  [IPCEventType.accessibilityPermissionsStatus]: PermissionsStatusPayload,
  [IPCEventType.requestAccessibilityPermissions]: null,
  [IPCEventType.requestMainWindowPresenting]: null,
  [IPCEventType.requestOpenSettings]: null,
  [IPCEventType.requestAppQuiting]: null,
  [IPCEventType.quitObserverProcess]: null,
  [IPCEventType.traverseApp]: TraverseAppPayload,
  [IPCEventType.authTokensExpired]: AuthTokensExpiredPayload,
  [IPCEventType.clearAuth]: ClearAuthPayload,
  [IPCEventType.updateStatusChanged]: UpdateStatusPayload,
  [IPCEventType.manualSnapshot]: null,
  [IPCEventType.pasteText]: PasteTextPayload,
  [IPCEventType.installAppUpdate]: null,
  [IPCEventType.deleteContext]: DeleteContextPayload,
  [IPCEventType.resourcesSnapshot]: null,
  [IPCEventType.getMentionableWindows]: MentionableWindowsPayload,
  [IPCEventType.getCapturableWindows]: CapturableWindowsPayload,
  [IPCEventType.getFrontAppContext]: FrontAppContextPayload,
  [IPCEventType.mentionableWindowsReceived]: MentionableWindowsPayload,
  [IPCEventType.capturableWindowsReceived]: CapturableWindowsPayload,
  [IPCEventType.captureAppScreenshot]: AppScreenshotCapturedPayload,
  [IPCEventType.appScreenshotCaptured]: AppScreenshotCapturedPayload,
  [IPCEventType.setSubscriptionPlan]: SubscriptionPlanPayload,
  [IPCEventType.setBackendChoice]: BackendChoicePayloadSchema,
  [IPCEventType.startEventKitCoordinator]: null,
  [IPCEventType.stopEventKitCoordinator]: null,
  [IPCEventType.restartEventKitCoordinator]: null,
  [IPCEventType.getEventKitReminderLists]: AcknowledgePayload,
  [IPCEventType.startContactsCoordinator]: null,
  [IPCEventType.stopContactsCoordinator]: null,
  [IPCEventType.restartContactsCoordinator]: null,
  [IPCEventType.getSnapshotDumpApps]: SnapshotDumpAppsPayload,
  [IPCEventType.snapshotDumpAppsReceived]: SnapshotDumpAppsPayload,
  [IPCEventType.getSnapshotDumpArchives]: SnapshotDumpArchivesPayload,
  [IPCEventType.snapshotDumpArchivesReceived]: SnapshotDumpArchivesPayload,
  [IPCEventType.enableDebugStream]: null,
  [IPCEventType.disableDebugStream]: null,
  [IPCEventType.presentAccessibilityPermissionAssistant]: null,
  [IPCEventType.dismissAccessibilityPermissionAssistant]: null,

  // NOTE: The code currently conflates in `ipcEventTypeToPayloadMap` both request and response schemas for IPC
  // messages. It's (to me) kind of an accident that it all "type checks" and works. But maybe I just
  // don't get it.
  [IPCEventType.invokePlatformAction]: AcknowledgePayload,
  [IPCEventType.startNoteTaking]: AcknowledgePayload,
  [IPCEventType.stopNoteTaking]: AcknowledgePayload,
  [IPCEventType.meetingHeartbeat]: AcknowledgePayload,
  [IPCEventType.meetingSpeakerSightings]: MeetingSpeakerSightingsPayloadSchema,
  [IPCEventType.meetingLocalTranscriptionReadinessGet]: AcknowledgePayload,
  [IPCEventType.meetingLocalTranscriptionReadinessRetry]: AcknowledgePayload,
  [IPCEventType.meetingGetTranscriptionInputDevices]: AcknowledgePayload,
  [IPCEventType.meetingSetTranscriptionInputDevice]: AcknowledgePayload,
  [IPCEventType.meetingPostProcessingStart]: AcknowledgePayload,
  [IPCEventType.meetingPostProcessingRecoverableRecordings]: AcknowledgePayload,
  [IPCEventType.meetingPostProcessingAcknowledgeResult]: AcknowledgePayload,
  [IPCEventType.meetingPostProcessingSyncState]: AcknowledgePayload,
  [IPCEventType.meetingTranscriptDelta]: TranscriptDeltaPayloadSchema,
  [IPCEventType.dictationTranscriptDelta]: DictationTranscriptPayloadSchema,
  [IPCEventType.dictationSessionStateChanged]:
    DictationSessionStatePayloadSchema,
  [IPCEventType.dictationAudioLevel]: DictationAudioLevelPayloadSchema,
  [IPCEventType.meetingIntentOverlayShow]: MeetingIntentDetectedPayloadSchema,
  [IPCEventType.meetingIntentOverlayReady]: AcknowledgePayload,
  [IPCEventType.meetingIntentOverlayWillShow]:
    MeetingIntentDetectedPayloadSchema,
  [IPCEventType.meetingIntentOverlayPresented]:
    MeetingIntentDetectedPayloadSchema,
  [IPCEventType.meetingIntentOverlayResolve]:
    MeetingIntentOverlayResolutionSchema,
  [IPCEventType.startDictation]: AcknowledgePayload,
  [IPCEventType.stopDictation]: AcknowledgePayload,
};

export const ipcEventTypeToRequestPayloadMap: Partial<
  Record<IPCEventType, z.ZodType<Record<string, unknown>>>
> = {
  [IPCEventType.getMentionableWindows]: z.object({}).strict(),
  [IPCEventType.getCapturableWindows]: z.object({}).strict(),
  [IPCEventType.invokePlatformAction]: PlatformActionInvocationPayloadSchema,
  [IPCEventType.startNoteTaking]: StartNoteTakingEventPayloadSchema,
  [IPCEventType.stopNoteTaking]: StopNoteTakingRequestPayloadSchema,
  [IPCEventType.startDictation]: StartDictationPayloadSchema,
  [IPCEventType.stopDictation]: StopDictationPayloadSchema,
  [IPCEventType.meetingPostProcessingStart]:
    MeetingPostProcessingStartPayloadSchema,
  [IPCEventType.meetingPostProcessingAcknowledgeResult]:
    MeetingPostProcessingJobReferencePayloadSchema,
  [IPCEventType.meetingPostProcessingSyncState]:
    MeetingPostProcessingJobReferencePayloadSchema,
  [IPCEventType.getSnapshotDumpApps]: GetSnapshotDumpAppsPayload,
  [IPCEventType.getSnapshotDumpArchives]: GetSnapshotDumpArchivesPayload,
};

// Type map with inferred types for all event payloads
export type IPCEventTypeToInferredPayloadMap = {
  [K in IPCEventType]: K extends keyof typeof ipcEventTypeToPayloadMap
    ? (typeof ipcEventTypeToPayloadMap)[K] extends z.ZodSchema
      ? z.infer<(typeof ipcEventTypeToPayloadMap)[K]>
      : (typeof ipcEventTypeToPayloadMap)[K]
    : never;
};

// Define which events are inbound vs outbound based on their semantic meaning
const inboundEventTypes = new Set([
  IPCEventType.ping,
  IPCEventType.acknowledge,
  IPCEventType.meetingAppsUpdated,
  IPCEventType.meetingParticipantsUpdated,
  IPCEventType.meetingSpeakerNameResolved,
  IPCEventType.contextKitDidCrash,
  IPCEventType.contextKitDidRecover,
  IPCEventType.meetingStopped,
  IPCEventType.meetingLocalTranscriptionReadinessChanged,
  IPCEventType.meetingPostProcessingStateChanged,
  IPCEventType.meetingRequestQuickNote,
  IPCEventType.meetingRequestPrepCalendarEvent,
  IPCEventType.meetingRequestStartCalendarEvent,
  IPCEventType.quitBlockedByMeeting,
  IPCEventType.meetingTranscriptionInputDeviceChanged,
  IPCEventType.systemEventReceived,
  IPCEventType.meetingAudioEnergyLevelsReceived,
  IPCEventType.spriteUpdated,
  IPCEventType.apiSenderEventReceived,
  IPCEventType.exclusionUpdated,
  IPCEventType.requestAuthToken,
  IPCEventType.disableContextCollection,
  IPCEventType.enableContextCollection,
  IPCEventType.installAppUpdate,
  IPCEventType.traversedAppContent,
  IPCEventType.frontAppContext,
  IPCEventType.excludeApp,
  IPCEventType.excludeDomain,
  IPCEventType.includeApp,
  IPCEventType.includeDomain,
  IPCEventType.resourcesSnapshotReceived,
  IPCEventType.readabilityIssue,
  IPCEventType.modifierTapDetected,
  IPCEventType.modifierHoldStarted,
  IPCEventType.modifierHoldReleased,
  IPCEventType.mentionableWindowsReceived,
  IPCEventType.capturableWindowsReceived,
  IPCEventType.appScreenshotCaptured,
  IPCEventType.meetingTranscriptDelta,
  IPCEventType.meetingSegmentEvent,
  IPCEventType.dictationTranscriptDelta,
  IPCEventType.dictationSessionStateChanged,
  IPCEventType.dictationAudioLevel,
  IPCEventType.meetingInterrupt,
  IPCEventType.meetingStartInputSilent,
  IPCEventType.snapshotDumpAppsReceived,
  IPCEventType.snapshotDumpArchivesReceived,
  IPCEventType.debugSnapshotReport,
]);

const outboundEventTypes = new Set([
  IPCEventType.ping,
  IPCEventType.isObserverRunning,
  IPCEventType.configureModifierTapMonitor,
  IPCEventType.startModifierTapMonitor,
  IPCEventType.stopModifierTapMonitor,
  IPCEventType.updateContentFilters,
  IPCEventType.syncExclusions,
  IPCEventType.setMeetingDetectionDisabledApps,
  IPCEventType.setMeetingDetectionDisabledSites,
  IPCEventType.runObserver,
  IPCEventType.stopObserver,
  IPCEventType.setTokenAndUrl,
  IPCEventType.setUserEmail,
  IPCEventType.setFeatureFlags,
  IPCEventType.accessibilityPermissionsStatus,
  IPCEventType.requestAccessibilityPermissions,
  IPCEventType.requestMainWindowPresenting,
  IPCEventType.requestOpenSettings,
  IPCEventType.requestAppQuiting,
  IPCEventType.quitObserverProcess,
  IPCEventType.startNoteTaking,
  IPCEventType.stopNoteTaking,
  IPCEventType.traverseApp,
  IPCEventType.authTokensExpired,
  IPCEventType.clearAuth,
  IPCEventType.updateStatusChanged,
  IPCEventType.manualSnapshot,
  IPCEventType.pasteText,
  IPCEventType.startDictation,
  IPCEventType.stopDictation,
  IPCEventType.deleteContext,
  IPCEventType.resourcesSnapshot,
  IPCEventType.getMentionableWindows,
  IPCEventType.getCapturableWindows,
  IPCEventType.getFrontAppContext,
  IPCEventType.captureAppScreenshot,
  IPCEventType.meetingHeartbeat,
  IPCEventType.meetingSpeakerSightings,
  IPCEventType.meetingLocalTranscriptionReadinessGet,
  IPCEventType.meetingLocalTranscriptionReadinessRetry,
  IPCEventType.meetingGetTranscriptionInputDevices,
  IPCEventType.meetingSetTranscriptionInputDevice,
  IPCEventType.meetingPostProcessingStart,
  IPCEventType.meetingPostProcessingRecoverableRecordings,
  IPCEventType.meetingPostProcessingAcknowledgeResult,
  IPCEventType.meetingPostProcessingSyncState,
  IPCEventType.setSubscriptionPlan,
  IPCEventType.setBackendChoice,
  IPCEventType.meetingMenubarCalendarConfigUpdated,
  IPCEventType.meetingMenubarCalendarDataUpdated,
  IPCEventType.startEventKitCoordinator,
  IPCEventType.stopEventKitCoordinator,
  IPCEventType.restartEventKitCoordinator,
  IPCEventType.getEventKitReminderLists,
  IPCEventType.startContactsCoordinator,
  IPCEventType.stopContactsCoordinator,
  IPCEventType.restartContactsCoordinator,
  IPCEventType.getSnapshotDumpApps,
  IPCEventType.getSnapshotDumpArchives,
  IPCEventType.enableDebugStream,
  IPCEventType.disableDebugStream,
  IPCEventType.presentAccessibilityPermissionAssistant,
  IPCEventType.dismissAccessibilityPermissionAssistant,
  IPCEventType.invokePlatformAction,
]);

// Programmatically generate keys arrays from the enum
const inboundKeys = Object.keys(IPCEventType).filter((key) =>
  inboundEventTypes.has(IPCEventType[key as keyof typeof IPCEventType])
) as Array<keyof typeof IPCEventType>;

const outboundKeys = Object.keys(IPCEventType).filter((key) =>
  outboundEventTypes.has(IPCEventType[key as keyof typeof IPCEventType])
) as Array<keyof typeof IPCEventType>;

// Extract enum subsets
export const inboundEventKeys = Object.fromEntries(
  inboundKeys.map((key) => [key, IPCEventType[key]])
) as Pick<typeof IPCEventType, (typeof inboundKeys)[number]>;

export const outboundEventKeys = Object.fromEntries(
  outboundKeys.map((key) => [key, IPCEventType[key]])
) as Pick<typeof IPCEventType, (typeof outboundKeys)[number]>;

// Unified payload mapping for all event types

// Zod enums
export const IPCEventTypeEnum = z.nativeEnum(IPCEventType);
